import { FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Badge,
  BoxV2 as Box,
  ButtonV2 as Button,
  Drawer,
  Flex,
  RoleGuard,
  Table,
  usePermission,
} from 'portal-commons';
import { useParams } from 'react-router-dom';
import {
  faCircleX,
  faDownload,
  faEye,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  campaignController,
  enumController,
  CampaignDetail,
  CampaignOperation,
  AttachmentFolder,
  CspCampaignSharing,
  Loa,
  resellerController,
  Reseller,
  senderIdInventoryController,
  SenderIdInventory,
  MnoEnum,
  UseCase,
} from '@/apis/core';
import { PageContainer, LoaPreviewModal } from '@/components';
import { Loader } from '@/shared_elements';
import { toastFlashMessage } from '@/utils';
import { generateAndDownloadFile } from '@/utils/download';
import { humanize } from '@/utils/string';
import { convertTimeWithTimezone } from '@/utils/time';
import { getBadgeTypeByStatus } from '@/applications/CspPortal/Brands/utils';
import { ConfirmationDialog, AttachmentUploader } from '../../components';

import {
  DetailField,
  Section,
  BlockDivider,
  SectionGrid,
  SummarySection,
  ProgramDetailsForm,
  SampleMessagesForm,
  SenderIdPreferenceSection,
  ElectCnpForm,
  ProgramAttributesSection,
} from '../../components/CampaignDetailHU';

const CarrierStatusHeaders = [
  { id: 'mno', label: 'MNO', sortable: false },
  { id: 'senderId', label: 'SENDER ID', sortable: false },
  { id: 'status', label: 'STATUS', sortable: false },
  { id: 'electedDca', label: 'ELECTED DCA', sortable: false },
];

const LoasTableHeader = [
  { id: 'fileName', label: 'DOCUMENT', sortable: false },
  { id: 'status', label: 'STATUS', sortable: false },
  { id: 'createDate', label: 'CREATED DATE', sortable: false },
  { id: 'completionDate', label: 'COMPLETED DATE', sortable: false },
  { id: 'actions', label: 'ACTIONS', sortable: false },
];

const ResponsiblePartiesTableHeader = [
  { id: 'cnp', label: 'Connectivity Partner', sortable: false },
  { id: 'reseller', label: 'Reseller', sortable: false },
];

interface RouteParams {
  id: string;
}

enum DrawerItemType {
  EditProgramDetails = 'editProgramDetails',
  EditSampleMessage = 'editSampleMessages',
  ElectCNP = 'electCNP',
}

const NO_RESELLER_ID = 'R000000';

const CampaignDetailHU: FC = () => {
  const { id: campaignUid } = useParams<RouteParams>();
  const { hasPermission } = usePermission();

  const [loader, setLoader] = useState(true);
  const [campaign, setCampaign] = useState<CampaignDetail>();
  const [drawerItemType, setDrawerItemType] = useState<DrawerItemType | null>(
    null
  );

  const [mnoStatusInfo, setMnoStatusInfo] = useState<CampaignOperation[]>([]);
  const [loas, setLoas] = useState<Loa[]>([]);
  const [senderIdData, setSenderIdData] = useState<SenderIdInventory[]>([]);
  const [electedDcas, setElectedDcas] = useState<number[]>([]);
  const [mnoList, setMnoList] = useState<MnoEnum[]>([]);
  const [useCases, setUseCases] = useState<UseCase[]>([]);

  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [loasProceeding, setLoasProceeding] = useState(false);
  const [isCampaignUpdating, setIsCampaignUpdating] = useState(false);
  const [nominationInfo, setNominationInfo] = useState<CspCampaignSharing>();
  const [previewLoaUid, setPreviewLoaUid] = useState<string | null>(null);
  const [reseller, setReseller] = useState<Reseller>();

  const fetchData = async (uid: string) => {
    setLoader(true);
    try {
      const campaignData = await campaignController.getCampaignDetail(uid);
      if (!campaignData) {
        throw new Error('Campaign not found');
      }
      setCampaign(campaignData);

      const [
        operationsResult,
        loasResult,
        electedDcaResult,
        mnoListResult,
        useCasesResult,
      ] = await Promise.allSettled([
        campaignController.getCampaignOperations(uid),
        campaignController.getCampaignLoas(uid),
        campaignController.getCampaignElectedDcas(uid),
        enumController.getMNOsByCountry(),
        enumController.getUseCases({ channelId: 'SMS' }),
      ]);

      setMnoStatusInfo(
        operationsResult.status === 'fulfilled'
          ? operationsResult.value || []
          : []
      );
      setLoas(loasResult.status === 'fulfilled' ? loasResult.value || [] : []);
      setElectedDcas(
        electedDcaResult.status === 'fulfilled'
          ? electedDcaResult.value || []
          : []
      );
      setMnoList(
        mnoListResult.status === 'fulfilled' ? mnoListResult.value || [] : []
      );
      setUseCases(
        useCasesResult.status === 'fulfilled' ? useCasesResult.value || [] : []
      );
    } catch (e) {
      console.error('Error fetching campaign details: ', e);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (campaignUid) {
      fetchData(campaignUid);
      updateNominationStatus(campaignUid);
    }
  }, [campaignUid]);

  useEffect(() => {
    if (campaign?.resellerUid && campaign.resellerUid !== NO_RESELLER_ID) {
      resellerController.getReseller(campaign?.resellerUid).then((res) => {
        setReseller(res);
      });
    }
  }, [campaign?.resellerUid]);

  useEffect(() => {
    if (campaignUid && mnoStatusInfo && mnoStatusInfo.length > 0) {
      senderIdInventoryController
        .getCampaignSenderIds(campaignUid, undefined, { silent: true })
        .then((res) => {
          setSenderIdData(res);
        });
    }
  }, [campaignUid, mnoStatusInfo]);

  // Parse dynamic target networks
  const targetMnos = useMemo(() => {
    const campaignTargetMNOs = campaign?.targetMNOs;
    if (Array.isArray(campaignTargetMNOs) && campaignTargetMNOs.length > 0) {
      return campaignTargetMNOs.map((networkId) => {
        const matchedMno = mnoList?.find(
          (m) => Number(m.networkId) === networkId
        );
        return {
          networkId,
          mno: matchedMno?.displayName || `MNO ${networkId}`,
        };
      });
    }
    return [];
  }, [campaign?.targetMNOs, mnoList]);

  const carrierStatusRows = useMemo(() => {
    return (mnoStatusInfo || []).map((operation) => {
      const matchedMno = mnoList?.find(
        (m) => Number(m.networkId) === operation.networkId
      );
      return {
        mnoName:
          operation.rulesEngineResultsJson?.mno ||
          matchedMno?.displayName ||
          `MNO ${operation.networkId}`,
        senderId:
          senderIdData.find((d) => d.networkId === operation.networkId)
            ?.senderIdValue || 'N/A',
        status: operation.status,
        electedDca: electedDcas.includes(operation.networkId) ? 'Yes' : 'No',
        networkId: operation.networkId,
      };
    });
  }, [mnoStatusInfo, mnoList, senderIdData, electedDcas]);

  const carrierStatusTableData = useMemo(() => {
    return carrierStatusRows.map((row) => {
      return {
        mno: <span>{row.mnoName}</span>,
        senderId: (
          <span>
            {senderIdData.find((d) => d.networkId === row.networkId)
              ?.senderIdValue || 'N/A'}
          </span>
        ),
        status: (
          <Badge type={getBadgeTypeByStatus(row.status)}>
            {humanize(row.status)}
          </Badge>
        ),
        electedDca: <span>{row.electedDca}</span>,
      };
    });
  }, [carrierStatusRows, senderIdData]);

  const handleLoaDownload = async (loa: Loa) => {
    setLoasProceeding(true);
    const response = await campaignController.downloadLoa(
      campaignUid,
      loa.docUid
    );
    if (response) {
      // the file name should align with that in the LOA table
      const file = new File([response.data], response.filename || 'LOA.pdf', {
        type: 'application/pdf',
      });
      generateAndDownloadFile(file);
    }
    setLoasProceeding(false);
  };

  const updateNominationStatus = async (uid: string) => {
    const data = await campaignController.getCampaignSharingStatus(uid);

    if (data) {
      setNominationInfo(data);
      if (data.status) {
        setDrawerItemType(
          ['DECLINED'].includes(data.status) ? DrawerItemType.ElectCNP : null
        );
      } else {
        setDrawerItemType(DrawerItemType.ElectCNP);
      }
    }
  };

  const previewLoa = (docUid: string) => {
    if (docUid) {
      setPreviewLoaUid(docUid);
    }
  };

  const isLoaDownloadable = (loaStatus: string) => {
    return ['COMPLETED', 'SENT_FOR_SIGNATURE'].includes(loaStatus);
  };

  const loasTableData = useMemo(() => {
    return loas.map((loa) => ({
      fileName: (
        <span data-testid="fileName">
          {/* replace static filename if API returns it */}
          {isLoaDownloadable(loa.status) ? 'LOA.pdf' : 'N/A'}
        </span>
      ),
      status: (
        <span>
          {loa.status ? (
            <Badge type={getBadgeTypeByStatus(loa.status)} data-testid="status">
              {humanize(loa.status)}
            </Badge>
          ) : (
            'N/A'
          )}
        </span>
      ),
      createDate: (
        <span data-testid="createDate">
          {convertTimeWithTimezone(loa.createDate, undefined, 'MM/DD/YYYY') ||
            'N/A'}
        </span>
      ),
      completionDate: (
        <span data-testid="signDate">
          {loa.signDate
            ? convertTimeWithTimezone(loa.signDate, undefined, 'MM/DD/YYYY')
            : 'N/A'}
        </span>
      ),
      actions: isLoaDownloadable(loa.status) ? (
        <Flex
          sx={{
            color: '#333E41',
            fontSize: '16px',
            gap: '8px',
            svg: { cursor: 'pointer' },
          }}
        >
          <RoleGuard feature="brandDetail.downloadLoa">
            <FontAwesomeIcon
              fontSize="16"
              role="button"
              data-testid="downloadCampaignLoa"
              icon={faDownload}
              onClick={() => {
                if (!loasProceeding) {
                  handleLoaDownload(loa);
                }
              }}
            />
          </RoleGuard>
          <RoleGuard feature="brandDetail.previewLoa">
            <FontAwesomeIcon
              fontSize="16"
              role="button"
              data-testid="viewCampaignLoa"
              icon={faEye}
              onClick={() => {
                if (!loasProceeding) {
                  previewLoa(loa.docUid);
                }
              }}
            />
          </RoleGuard>
        </Flex>
      ) : null,
    }));
  }, [loas, loasProceeding]);

  const responsiblePartiesTableData = useMemo(() => {
    const upstreamCnp = (() => {
      if (!nominationInfo) return 'NONE';
      if (nominationInfo.status === 'DECLINED') {
        return '--';
      }
      return (
        nominationInfo.upstreamCnpName ||
        nominationInfo.upstreamCnpUid ||
        'NONE'
      );
    })();

    const showElectButton =
      campaign &&
      campaign.status !== 'EXPIRED' &&
      (!nominationInfo?.status || nominationInfo.status === 'DECLINED');

    const resellerName = (() => {
      if (reseller?.companyName) return reseller.companyName;
      if (campaign?.resellerUid === NO_RESELLER_ID) return 'No Reseller';
      return campaign?.resellerName || 'N/A';
    })();

    return [
      {
        cnp: (
          <Flex sx={{ alignItems: 'center', gap: '8px' }}>
            <span>{upstreamCnp}</span>
            {hasPermission('campaignDetail.electCnp') && showElectButton && (
              <Button
                type="button"
                size="sm"
                variant="fill"
                color="primary"
                shape="square"
                onClick={() => setDrawerItemType(DrawerItemType.ElectCNP)}
                data-testid="electCnpButton"
              >
                Elect CNP
              </Button>
            )}
          </Flex>
        ),
        reseller: <span>{resellerName} </span>,
      },
    ];
  }, [campaign, nominationInfo, reseller]);

  const handleDeactivateCampaign = async () => {
    setDeactivateLoading(true);
    const response = await campaignController.deactivateCampaign(campaignUid);
    if (response) {
      toastFlashMessage('Program deactivated successfully', 'success');
      // workaround: API not returning brand info like that in GET method, so backfill the data
      setCampaign({
        ...response,
        brandName: campaign?.brandName,
        brand: campaign?.brand,
      });
      setDeactivateDialogOpen(false);
    }
    setDeactivateLoading(false);
  };

  const updateRemoteCampaign = async (uid: string, data: any) => {
    const res = await campaignController.updateCampaign(uid, data);
    if (res) {
      return { ...campaign, ...data };
    }
    return null;
  };

  const updateCampaign = async (
    uid: string,
    data: any,
    options?: {
      optimistic?: { key: string; value: any };
      successMessage?: string;
      closeDrawer?: boolean;
      showDrawerLoading?: boolean;
    }
  ) => {
    if (!campaign) return;
    const originalCampaign = { ...campaign };

    const {
      optimistic,
      successMessage = 'Program updated successfully',
      closeDrawer = false,
      showDrawerLoading = false,
    } = options || {};

    if (optimistic) {
      setCampaign({
        ...campaign,
        [optimistic.key]: optimistic.value,
      });
    }

    if (showDrawerLoading) {
      setIsCampaignUpdating(true);
    }

    const newCampaign = await updateRemoteCampaign(uid, data);
    if (newCampaign) {
      setCampaign(newCampaign);
      if (successMessage) {
        toastFlashMessage(successMessage, 'success');
      }
      if (closeDrawer) {
        setDrawerItemType(null);
      }
    } else {
      setCampaign(originalCampaign);
    }

    if (showDrawerLoading) {
      setIsCampaignUpdating(false);
    }
  };

  const handleAttributeChange = async (
    value: boolean,
    key: string,
    label: string
  ) => {
    if (campaign && campaign[key] !== value) {
      await updateCampaign(
        campaignUid,
        { [key]: value },
        {
          optimistic: { key, value },
          successMessage: `${label} attribute updated`,
        }
      );
    }
  };

  const handleInlineUpdate = async (data: any) => {
    await updateCampaign(campaignUid, data);
  };

  const sampleMessages = useMemo(() => {
    if (campaign) {
      return [
        campaign?.sample1,
        campaign?.sample2,
        campaign?.sample3,
        campaign?.sample4,
        campaign?.sample5,
      ].filter((s) => !!s);
    }
    return [];
  }, [campaign]);

  const handleElectCnp = async (cnpUid: string) => {
    setIsCampaignUpdating(true);
    const response = await campaignController.electCampaignWithCnp(
      campaignUid,
      cnpUid
    );
    if (response) {
      toastFlashMessage('Connectivity Partner successfully elected', 'success');
      await updateNominationStatus(campaignUid);
      setDrawerItemType(null);
      const updated = await campaignController.getCampaignDetail(campaignUid);
      if (updated) setCampaign(updated);
    }
    setIsCampaignUpdating(false);
  };

  const expiredCampaign = campaign?.status === 'EXPIRED';

  return (
    <div data-testid="campaignDetailHU">
      {loader || !campaign ? (
        <Loader />
      ) : (
        <PageContainer
          sx={{
            gap: '32px',
            padding: '10px 66px 20px 71px',
            lineHeight: 'normal',
          }}
        >
          <Flex
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div style={{ marginBottom: '12px' }}>
              <Box
                sx={{
                  fontSize: '32px',
                  color: '#19262a',
                  fontWeight: 'semiBold',
                  mb: '8px',
                }}
              >
                Messaging Program
                <Box
                  sx={{
                    fontSize: '16px',
                    color: '#4D5659',
                    mt: '4px',
                    fontWeight: 'normal',
                  }}
                >
                  Program ID: {campaign?.uid}
                </Box>
              </Box>
            </div>

            <Button
              type="button"
              variant="fill"
              size="lg"
              shape="square"
              color="primary"
              startAdornment={faCircleX}
              onClick={() => setDeactivateDialogOpen(true)}
              disabled={
                expiredCampaign ||
                !hasPermission('campaignDetail.viewMoreActions')
              }
            >
              Deactivate Program
            </Button>
          </Flex>

          <SummarySection
            campaign={campaign}
            useCases={useCases}
            handleAttributeChange={handleAttributeChange}
            expiredCampaign={expiredCampaign}
          />

          <BlockDivider />

          <Section
            title="Letters of Attestation"
            sx={{
              '& table': {
                '& th:last-child': { width: '80px' },
                '& td:nth-child(2)': { py: 'unset' },
              },
            }}
          >
            <Table
              disableHover
              loading={loader || loasProceeding}
              headRows={LoasTableHeader}
              data={loasTableData}
              emptyState=""
              emptyBlockHeight="120px"
              testId="loaTable"
            />
          </Section>

          <BlockDivider />

          <SenderIdPreferenceSection
            campaign={campaign}
            senderIdData={senderIdData}
          />

          <BlockDivider />

          <Section title="Carrier Status">
            <Table
              disableHover
              loading={loader}
              headRows={CarrierStatusHeaders}
              data={carrierStatusTableData}
              emptyState=""
              emptyBlockHeight="120px"
              testId="carrierStatusTable"
            />

            <Section title="Target Networks">
              <Flex sx={{ gap: 's', flexWrap: 'wrap' }}>
                {targetMnos.map((net, index) => (
                  <Box
                    key={net.networkId}
                    sx={{
                      bg: '#808789',
                      color: '#fff',
                      borderRadius: '100px',
                      padding: '6px 16px',
                      fontSize: '12px',
                    }}
                    data-testid={`targetMno-${index}`}
                  >
                    {net.mno}
                  </Box>
                ))}
              </Flex>
            </Section>
          </Section>

          <BlockDivider />

          <ProgramAttributesSection
            campaign={campaign}
            readonly={expiredCampaign}
            handleAttributeChange={handleAttributeChange}
            onDataChange={handleInlineUpdate}
          />

          <BlockDivider />

          <Section
            title="Program Details"
            editable={!!hasPermission('campaignDetail.editCampaignDetails')}
            editDisabled={expiredCampaign}
            onEdit={() => setDrawerItemType(DrawerItemType.EditProgramDetails)}
          >
            <SectionGrid>
              <DetailField label="Program Description">
                {campaign?.description}
              </DetailField>
              <DetailField label="Expected Monthly Volume">
                {campaign?.expectedMessageVolume}
              </DetailField>
            </SectionGrid>
          </Section>

          <BlockDivider />

          <Section
            title="Multimedia Upload for Supporting Documents"
            description="Provides an area to upload any supporting information for opt-in, call-to-action, terms and conditions, privacy policy, etc."
          >
            <AttachmentUploader
              readonly={
                expiredCampaign ||
                !hasPermission('campaignDetail.addCtaMultimedia')
              }
              campaignUid={campaignUid}
              folder={AttachmentFolder.SupportingDocument}
              maxCount={5}
              disabledText={`Max 5 uploads is allowed`}
            />
          </Section>

          <BlockDivider />

          <Section
            title="Sample Messages"
            editable={!!hasPermission('campaignDetail.editSampleMessages')}
            editDisabled={expiredCampaign}
            onEdit={() => setDrawerItemType(DrawerItemType.EditSampleMessage)}
          >
            <SectionGrid
              sx={{
                gridTemplateColumns: '1fr 1fr',
                '> ol': {
                  listStylePosition: 'inside',
                  paddingLeft: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  fontSize: '14px',
                  color: '#313129',
                  wordBreak: 'break-all',
                },
              }}
            >
              {!!sampleMessages.length && (
                <ol>
                  {sampleMessages.map((msg, index) => (
                    <li key={index} data-testid={`sampleMessageItem-${index}`}>
                      {msg}
                    </li>
                  ))}
                </ol>
              )}
            </SectionGrid>
          </Section>

          <BlockDivider />

          <Section title="Other Responsible Parties">
            <Table
              disableHover
              loading={loader}
              headRows={ResponsiblePartiesTableHeader}
              data={responsiblePartiesTableData}
              emptyState=""
              emptyBlockHeight="80px"
              data-testid="responsiblePartiesTable"
            />
          </Section>

          <ConfirmationDialog
            open={deactivateDialogOpen}
            type="deactivate"
            handleCancel={() => setDeactivateDialogOpen(false)}
            handleSuccess={handleDeactivateCampaign}
            loader={deactivateLoading}
            text="Are you sure you want to deactivate this messaging program?"
          />

          <Drawer
            open={!!drawerItemType}
            initialScreenId={drawerItemType as string}
            onClose={() => setDrawerItemType(null)}
            width="35%"
            sx={{
              minWidth: '500px',
            }}
          >
            {drawerItemType === DrawerItemType.EditProgramDetails && (
              <ProgramDetailsForm
                campaign={campaign}
                onCancel={() => setDrawerItemType(null)}
                onSave={(data) => {
                  updateCampaign(campaignUid, data, {
                    closeDrawer: true,
                    showDrawerLoading: true,
                  });
                }}
                processing={isCampaignUpdating}
              />
            )}
            {drawerItemType === DrawerItemType.EditSampleMessage && (
              // follow US UX, sample messages count can only be added
              <SampleMessagesForm
                campaign={campaign}
                onCancel={() => setDrawerItemType(null)}
                onSave={(data) => {
                  updateCampaign(campaignUid, data, {
                    closeDrawer: true,
                    showDrawerLoading: true,
                  });
                }}
                processing={isCampaignUpdating}
              />
            )}
            {drawerItemType === DrawerItemType.ElectCNP && (
              <ElectCnpForm
                campaign={campaign}
                onCancel={() => setDrawerItemType(null)}
                onSave={handleElectCnp}
                processing={isCampaignUpdating}
              />
            )}
          </Drawer>
        </PageContainer>
      )}
      {previewLoaUid &&
        createPortal(
          <LoaPreviewModal
            readOnly
            campaignUid={campaignUid}
            docUid={previewLoaUid}
            onCancel={() => setPreviewLoaUid(null)}
          />,
          document.body
        )}
    </div>
  );
};

export default CampaignDetailHU;
